import { useCallback, useEffect, useMemo, useState } from "react";

import { createApiClient, type RuntimeConfig } from "./lib/api.js";
import { fetchSession, rememberStore, resolveSelectedStore, type Session, type SessionStore } from "./lib/session.js";
import { LoginScreen } from "./routes/LoginScreen.js";
import { SetPasswordScreen } from "./routes/SetPasswordScreen.js";
import { SignupScreen } from "./routes/SignupScreen.js";
import { ForgotPasswordScreen } from "./routes/ForgotPasswordScreen.js";
import { VerifyEmailScreen } from "./routes/VerifyEmailScreen.js";
import { DashboardShell } from "./routes/DashboardShell.js";
import { InstallResultScreen, installOutcomeFromUrl } from "./routes/InstallResultScreen.js";
import { InviteInstallScreen } from "./routes/InviteInstallScreen.js";

/**
 * The whole app's state machine, deliberately small:
 *
 *   checking  → asking /auth/me whether this browser already has a session
 *   signed-out→ the login screen
 *   signed-in → the dashboard
 *
 * NO SESSION IS INFERRED FROM localStorage. Legacy kept a `user` object in Redux hydrated
 * from storage, which meant the UI could believe someone was signed in after their token
 * had been revoked — the screen said one thing and every request said another. Here the
 * only source is `/auth/me`, and a 401 anywhere drops straight back to signed-out.
 */
type Phase = { kind: "checking" } | { kind: "signed-out" } | { kind: "signed-in"; session: Session };

export function App({ config }: { readonly config: RuntimeConfig }): React.JSX.Element {
  const [phase, setPhase] = useState<Phase>({ kind: "checking" });
  const [selected, setSelected] = useState<SessionStore | null>(null);
  const [billingNotice, setBillingNotice] = useState<string | null>(null);

  /** The one-time token in `/set-password?token=…`, read before React routing exists. */
  const setPasswordToken = useMemo(() => new URLSearchParams(window.location.search).get("token"), []);
  const isSetPasswordRoute = window.location.pathname === "/set-password";
  // Signup is a PRE-AUTH route, like set-password: a person with no session can reach it.
  const isSignupRoute = window.location.pathname === "/signup";
  const isVerifyEmailRoute = window.location.pathname === "/verify-email";
  const isForgotPasswordRoute = window.location.pathname === "/forgot-password";
  const verifyEmailToken = useMemo(() => new URLSearchParams(window.location.search).get("token"), []);
  // Install pages (Flow 2 and the gateway's install callback) are PRE-AUTH too: a client installing
  // through an agency's link has no Ecomally account at all.
  const isInviteRoute = window.location.pathname === "/install/invite";
  const inviteToken = useMemo(() => new URLSearchParams(window.location.search).get("token"), []);
  const isInstallResultRoute = window.location.pathname === "/install/complete" || window.location.pathname === "/install/already-connected";

  const client = useMemo(
    () =>
      createApiClient({
        baseURL: config.apiUrl,
        currentTenantId: () => selected?.tenantId ?? null,
        onUnauthenticated: () => {
          setPhase({ kind: "signed-out" });
          setSelected(null);
        },
        onBillingRequired: setBillingNotice,
      }),
    // `selected` is read through a closure, so the client is rebuilt when it changes —
    // otherwise the tenant header would keep naming the store chosen at first render.
    [config.apiUrl, selected],
  );

  const load = useCallback(async () => {
    try {
      const session = await fetchSession(client);
      setPhase({ kind: "signed-in", session });
      setSelected(resolveSelectedStore(session.stores));
    } catch (error) {
      /**
       * Fully handled with a defined outcome (.ai/02). A 401 has already routed through
       * `onUnauthenticated`; anything else — a network blip, a 500 — still means there is
       * no usable session to render, and the login screen is the honest response.
       *
       * There is no logger in a browser bundle and a console line lands in a merchant's
       * devtools, so the state change IS the report — the same reasoning .ai/02 already
       * makes for packages/tracker.
       */
      void error;
      setPhase({ kind: "signed-out" });
    }
  }, [client]);

  useEffect(() => {
    if (isSetPasswordRoute || isSignupRoute || isVerifyEmailRoute || isForgotPasswordRoute || isInviteRoute || isInstallResultRoute) {
      setPhase({ kind: "signed-out" });
      return;
    }
    void load();
    // Deliberately once: re-running on every `client` identity change would refetch the
    // session each time a store is selected.
     
  }, []);

  if (isSetPasswordRoute && setPasswordToken !== null) {
    return <SetPasswordScreen client={client} token={setPasswordToken} />;
  }

  if (isSignupRoute) {
    return <SignupScreen client={client} />;
  }

  if (isInviteRoute && inviteToken !== null) {
    return <InviteInstallScreen client={client} token={inviteToken} />;
  }

  if (isInstallResultRoute) {
    const { outcome, shop, joinToken } = installOutcomeFromUrl(window.location);
    return <InstallResultScreen client={client} outcome={outcome} shop={shop} joinToken={joinToken} />;
  }

  if (isForgotPasswordRoute) {
    return <ForgotPasswordScreen client={client} />;
  }

  if (isVerifyEmailRoute && verifyEmailToken !== null) {
    return <VerifyEmailScreen client={client} token={verifyEmailToken} />;
  }

  if (phase.kind === "checking") return <div className="centered muted">Loading…</div>;
  if (phase.kind === "signed-out") return <LoginScreen client={client} onSignedIn={() => void load()} />;

  return (
    <DashboardShell
      client={client}
      session={phase.session}
      selected={selected}
      features={config.features}
      billingNotice={billingNotice}
      onSelectStore={(store) => {
        rememberStore(store.tenantId);
        setSelected(store);
        setBillingNotice(null);
      }}
      onSignOut={async () => {
        await client.post("/auth/logout").catch(() => undefined);
        setPhase({ kind: "signed-out" });
        setSelected(null);
      }}
    />
  );
}
